// App.js
import Amplitude from '@analytics-services/amplitude';
import Beamer from '@analytics-services/beamer';
import Strollr from '@analytics-services/strollr';
import { HelpPreferenceContext } from '@contexts/help-preference-context';
import { SessionContext } from '@contexts/session-context';
import { SFConnectionProvider } from '@contexts/sf-connection-context';
import { CommandCenter } from '@modules/command-center';
import { Onboarding } from '@modules/onboarding';
import { GlobalStateProvider } from '@providers/global-context-provider';
import HelpPreferenceContextProvider from '@providers/help-preference-provider';
import RolesContextProvider from '@providers/roles-context-provider';
import SessionContextProvider from '@providers/session-context-provider';
import { WindowsAdvancedProvider } from '@providers/windows-advanced-provider';
import UserPreferenceContextProvider from '@providers/user-preference-provider';
import store from '@redux-hooks/system-templates';
import { PublicAuthenticatedRoutes } from '@routes/public-authendicate-routes';
import ErrorBoundary from '@shared/components/error-boundary';
import Fallback from '@shared/components/fallback';
import Sentry from '@shared/components/sentry';
import { ToastContainer, initTranslations } from '@testsigma/atoms';
import { PageLoader } from '@utils/common';
import { enableMapSet } from 'immer';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { AppRouter } from './routes';
import './style.scss';

import SessionService from '@services/session.service';
import { newRelicUtils } from '@utils/newrelic';

newRelicUtils.initialize();

initTranslations(
  `${
    import.meta.env.DEV
      ? `${window.location.origin}/ui`
      : import.meta.env.VITE_BASE_URL
  }/locales/en-US/translation.json`,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

//TODO
const publicAccessRoute = ['/ui/under_maintenance'];

const checkIfUrlIsWhitelisted = (url: string) => {
  return publicAccessRoute.includes(url);
};

const isWhitelisted = checkIfUrlIsWhitelisted(location.pathname);

enableMapSet();

const App = () => (
  <QueryClientProvider client={queryClient}>
    {isWhitelisted ? (
      <PublicAuthenticatedRoutes />
    ) : (
      <ErrorBoundary fallback={Fallback}>
        <Provider store={store}>
          <SessionContextProvider>
            <UserPreferenceContextProvider>
              <HelpPreferenceContextProvider>
                <RolesContextProvider>
                  <GlobalStateProvider>
                    <WindowsAdvancedProvider>
                      <SFConnectionProvider>
                        <AppContent />
                        <CommandCenter />
                      </SFConnectionProvider>
                    </WindowsAdvancedProvider>
                  </GlobalStateProvider>
                </RolesContextProvider>
              </HelpPreferenceContextProvider>
            </UserPreferenceContextProvider>
          </SessionContextProvider>
        </Provider>
      </ErrorBoundary>
    )}
  </QueryClientProvider>
);

const AppContent = () => {
  const { isSessionLoading, session } = useContext(SessionContext);
  const { helpPreference } = useContext(HelpPreferenceContext);
  const idleTimeoutRef = useRef(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Show scrollbar only when scrolling
  useEffect(() => {
    // Track a separate timeout per scrollable element
    const scrollTimeouts = new WeakMap<HTMLElement, any>();

    const handleScroll = (e: Event) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      // Mark only this element as currently scrolling
      target.classList.add('is-scrolling');

      const existingTimeout = scrollTimeouts.get(target);
      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }

      // Schedule removal of the scrolling state for this element
      const timeoutId = window.setTimeout(() => {
        if (target.isConnected) {
          target.classList.remove('is-scrolling');
        }
        scrollTimeouts.delete(target);
      }, 1000);

      scrollTimeouts.set(target, timeoutId);
    };

    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const props = useMemo(() => {
    return {
      accountUuid: session?.tenant?.identityServiceId,
      isIdleSessionOut: false,
      sessionOut: () => SessionService.logout(false),
      serviceUrl: import.meta.env.VITE_ID_SERVICE_URL,
    };
  }, [session?.tenant?.identityServiceId]);

  const updateProps = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (idleTimeoutRef.current?.updateProps) {
      idleTimeoutRef.current.updateProps(props);
    } else {
      timeoutRef.current = setTimeout(updateProps, 300);
    }
  }, [props]);

  useEffect(() => {
    updateProps();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [session?.tenant?.identityServiceId]);

  if (isSessionLoading) {
    return (
      <div className='flex w-full h-full'>
        <PageLoader />
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full w-full'>
      <Suspense fallback={<PageLoader />}>
        {
          <>
            {import.meta.env.VITE_ENABLE_AMPLITUDE === 'true' && <Amplitude />}
            {import.meta.env.VITE_ENABLE_STROLLR === 'true' && <Strollr />}
            {import.meta.env.VITE_ENABLE_SENTRY === 'true' && <Sentry />}
          </>
        }
        {helpPreference?.hasOnBoarding ? <Onboarding /> : <AppRouter />}
        <ToastContainer />
        <Beamer />
        <ts-idle-timeout ref={idleTimeoutRef}></ts-idle-timeout>
        <ts-last-activity></ts-last-activity>
      </Suspense>
    </div>
  );
};
export default App;

